import React, { ChangeEvent, useEffect, useState } from 'react';
import { Breadcrumbs, Input } from '@cokitchen/cokitchen-components';
import PageHeaderText from 'components/base/page-header-text';
import { ROUTE_KEYS } from 'utils/constants';
import { ReactComponent as CalendarIcon } from 'assets/icons/calendar-icon.svg';
import { ReactComponent as TimeIcon } from 'assets/icons/time-icon.svg';
import './styles.scss';
import useEditPostContext from 'contexts/edit-post.context';
import DatePicker from 'components/base/date-picker';
import moment from 'moment';
import TimeSelector from 'components/base/time-selector';
import PageLoader from 'components/base/page-loader';
import _ from 'lodash';

const EditPostTimeline = () => {
  const { editPostForm, setEditPostForm, setDisabled, postLoading, postToEdit, initialEditData } =
    useEditPostContext();
  const [showStartDateModal, setShowStartDateModal] = useState(false);
  const [showEndDateModal, setShowEndDateModal] = useState(false);
  const [noExpirationDate, setNoExpirationDate] = useState(!editPostForm.end_date);
  const breadcrumbs = [
    { name: 'Manage homepage', path: ROUTE_KEYS.HOME },
    { name: 'Edit post', path: ROUTE_KEYS.EDIT_POST }
  ];

  useEffect(() => {
    if (!editPostForm.start_date || (!noExpirationDate && !editPostForm.end_date)) {
      setDisabled(true);
    } else {
      const hasChanged = !_.isEqual(initialEditData, editPostForm);
      if (hasChanged) {
        setDisabled(false);
      } else {
        setDisabled(true);
      }
    }
  }, [editPostForm]);

  const handleNoExpiryDateChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setEditPostForm({
        ...editPostForm,
        end_date: undefined,
        end_time: null
      });
    }
    setNoExpirationDate(e.target.checked);
  };
  if (postLoading || !postToEdit) return <PageLoader />;
  return (
    <div className='mx-10 pt-8 max-w-[700px]'>
      {showStartDateModal && (
        <DatePicker
          isOpen={showStartDateModal}
          onClose={() => setShowStartDateModal(false)}
          title='Select Start Date'
          handleDateSelect={(value) =>
            setEditPostForm({ ...editPostForm, start_date: value, end_date: undefined })
          }
          minDate={new Date()}
        />
      )}
      {showEndDateModal && (
        <DatePicker
          isOpen={showEndDateModal}
          onClose={() => setShowEndDateModal(false)}
          title='Select Expiry Date'
          handleDateSelect={(value) => setEditPostForm({ ...editPostForm, end_date: value })}
          minDate={moment().isAfter(editPostForm.start_date) ? new Date() : editPostForm.start_date}
        />
      )}
      <Breadcrumbs breadcrumbs={breadcrumbs} />
      <div className='mt-8'>
        <PageHeaderText title='Edit Post' description='Edit a post lorem dolum ipsum' />
      </div>
      <div className='flex items-start w-full gap-4 mb-6'>
        <section className='flex flex-col gap-1.5 grow'>
          <p className='text-gray-6 text-sm'>Start date</p>
          <div
            className='border border-gray-19 rounded-md flex items-center  py-4 px-5 cursor-pointer h-[50px]'
            onClick={() => setShowStartDateModal(true)}>
            <CalendarIcon className='mr-2' />
            <p className='text-sm text-gray-6'>
              {editPostForm.start_date
                ? moment(editPostForm.start_date).format('YYYY-MM-DD')
                : 'YYYY-MM-DD'}
            </p>
          </div>
        </section>
        <section className='flex flex-col gap-1.5 grow'>
          <p className='text-gray-6 text-sm'>Start time</p>
          <div className='border border-gray-19 rounded-md flex items-center py-4 px-5 h-[50px]'>
            <TimeIcon className='mr-2' />
            <TimeSelector
              value={editPostForm.start_time}
              onChange={(value) => setEditPostForm({ ...editPostForm, start_time: value })}
            />
          </div>
        </section>
      </div>
      {!noExpirationDate && (
        <div className='flex items-start w-full gap-4'>
          <section className='flex flex-col gap-1.5 grow'>
            <p className='text-gray-6 text-sm'>Expiry date</p>
            <div
              className='border border-gray-19 rounded-md flex items-center  py-4 px-5 cursor-pointer h-[50px]'
              onClick={() => setShowEndDateModal(true)}>
              <CalendarIcon className='mr-2' />
              <p className='text-sm text-gray-6'>
                {editPostForm.end_date
                  ? moment(editPostForm.end_date).format('YYYY-MM-DD')
                  : 'YYYY-MM-DD'}
              </p>
            </div>
          </section>
          <section className='flex flex-col gap-1.5 grow'>
            <p className='text-gray-6 text-sm'>Stop time</p>
            <div className='border border-gray-19 rounded-md flex items-center py-4 px-5 h-[50px]'>
              <TimeIcon className='mr-2' />
              <TimeSelector
                value={editPostForm.end_time}
                onChange={(value) => setEditPostForm({ ...editPostForm, end_time: value })}
              />
            </div>
          </section>
        </div>
      )}
      <div className='flex items-center gap-2 mt-6'>
        <Input
          type='checkbox'
          checked={noExpirationDate}
          onChange={(e) => {
            handleNoExpiryDateChange(e);
          }}
        />
        <p className='font-medium'>No expiration date</p>
      </div>
    </div>
  );
};

export default EditPostTimeline;
